import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { MonitorSmartphone } from "lucide-react";
import { ContactFooter } from "@/components/ContactFooter";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [logoutReason, setLogoutReason] = useState(null);
  // Ritorno post-login (es. da /offerta): solo percorsi interni sicuri, altrimenti home.
  const [nextPath] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("next");
    return p && p.startsWith("/") && !p.startsWith("//") ? p : null;
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get("reason") || sessionStorage.getItem("logout_reason");
    if (reason === "session_replaced" || reason === "expired") {
      setLogoutReason(reason);
      sessionStorage.removeItem("logout_reason");
      // Pulisce l'URL senza ricaricare
      window.history.replaceState({}, "", "/login");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      navigate(nextPath || "/");
    } catch (err) {
      toast.error(errMsg(err, "Email o password non corretti"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-md mx-auto px-5 pt-[14vh] fade-up">
        <div className="flex items-center gap-2 mb-10">
          <span className="inline-block w-3 h-3 rounded-full bg-[color:var(--volt)] border border-black/10" aria-hidden="true" />
          <span className="font-extrabold tracking-tight uppercase">Propedeutico</span>
        </div>
        <h1 className="text-4xl font-extrabold tracking-tight leading-tight">Allénati.<br />Supera il test.</h1>
        <p className="mt-3 text-[color:var(--text-2)] leading-6">
          Preparati al test di idoneità del corso propedeutico per allenatori. 60 domande, 4 blocchi, 9/15 per passare.
        </p>

        {logoutReason && (
          <div className="mt-5 flex items-start gap-3 rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] p-4 fade-up" data-testid="login-logout-reason-banner" role="status">
            <MonitorSmartphone className="w-4 h-4 mt-0.5 shrink-0 text-[color:var(--text-2)]" aria-hidden="true" />
            <p className="text-sm leading-5">
              {logoutReason === "session_replaced"
                ? "Sei stato disconnesso perché è stato effettuato l'accesso da un altro dispositivo. È consentita una sola sessione attiva per account."
                : "Il tuo account risulta scaduto. Contatta l'amministratore del corso."}
            </p>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-semibold mb-1.5">Email</label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="w-full rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3.5 text-sm focus:border-[color:var(--ink)] outline-none transition-colors duration-200"
              placeholder="nome@esempio.it"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="password" className="block text-sm font-semibold">Password</label>
              <Link
                to="/password-dimenticata"
                className="text-xs font-bold text-[color:var(--text-2)] underline underline-offset-2 hover:text-[color:var(--ink)] transition-colors duration-200"
                data-testid="login-forgot-password-link"
              >
                Password dimenticata?
              </Link>
            </div>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="w-full rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3.5 text-sm focus:border-[color:var(--ink)] outline-none transition-colors duration-200"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="press w-full rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-3.5 min-h-[44px] hover:bg-black/85 disabled:opacity-60 transition-colors duration-200"
          >
            {busy ? "Accesso in corso…" : "Accedi"}
          </button>
        </form>

        <p className="mt-6 text-sm text-[color:var(--text-2)]">
          Non hai un account?{" "}
          <Link to="/register" className="font-bold text-[color:var(--ink)] underline underline-offset-2" data-testid="login-goto-register-link">
            Registrati
          </Link>
        </p>

        <ContactFooter />
        <p className="mt-6 pb-10 text-xs text-[color:var(--text-2)]">App di supporto alla preparazione. Non è un prodotto ufficiale FIGC.</p>
      </div>
    </div>
  );
}
